import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CurrencyInr, TrendUp, Wallet, CalendarCheck, HandCoins, Check } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function DoctorPanelPage() {
  const [doctors, setDoctors] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState('');
  const [earnings, setEarnings] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [settlements, setSettlements] = useState([]);

  useEffect(() => { api.getDoctors().then(r => setDoctors(r.data)); }, []);

  const loadData = () => {
    if (!selectedDoc) return;
    setLoading(true);
    Promise.all([
      api.getDoctorEarnings(selectedDoc),
      api.getDoctorLedger(selectedDoc),
      api.getDoctorSettlements(selectedDoc)
    ]).then(([eRes, lRes, sRes]) => {
      setEarnings(eRes.data);
      setLedger(lRes.data);
      setSettlements(sRes.data);
      setLoading(false);
    });
  };

  useEffect(() => { loadData(); }, [selectedDoc]);

  return (
    <div className="p-6 space-y-4" data-testid="doctor-panel">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Doctor Panel</h1>
        <p className="text-sm mt-1" style={{ color: '#8A94A6' }}>Earnings, commissions, and ledger</p>
      </div>

      <div>
        <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Select Doctor</Label>
        <Select value={selectedDoc} onValueChange={setSelectedDoc}>
          <SelectTrigger className="w-80 h-9 mt-1"><SelectValue placeholder="Choose a doctor" /></SelectTrigger>
          <SelectContent>
            {doctors.map(d => <SelectItem key={d.id} value={d.id}>{d.name} ({d.specialization})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!selectedDoc ? (
        <div className="p-12 text-center rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <p className="text-sm" style={{ color: '#8A94A6' }}>Select a doctor to view earnings and ledger</p>
        </div>
      ) : loading ? (
        <p style={{ color: '#8A94A6' }}>Loading...</p>
      ) : earnings && (
        <>
          {/* Earnings Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
              <div className="flex items-center gap-2 mb-1"><TrendUp size={16} weight="duotone" style={{ color: '#007AFF' }} /><span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Total Sales</span></div>
              <p className="text-2xl font-bold" style={{ color: '#0A2540', fontVariantNumeric: 'tabular-nums' }}>₹{earnings.total_sales.toLocaleString()}</p>
            </div>
            <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
              <div className="flex items-center gap-2 mb-1"><CurrencyInr size={16} weight="duotone" style={{ color: '#34C759' }} /><span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Total Commission ({earnings.commission_percent}%)</span></div>
              <p className="text-2xl font-bold" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>₹{earnings.total_commission.toLocaleString()}</p>
            </div>
            <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
              <div className="flex items-center gap-2 mb-1"><Wallet size={16} weight="duotone" style={{ color: '#007AFF' }} /><span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Paid</span></div>
              <p className="text-2xl font-bold" style={{ color: '#007AFF', fontVariantNumeric: 'tabular-nums' }}>₹{earnings.total_paid.toLocaleString()}</p>
            </div>
            <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
              <div className="flex items-center gap-2 mb-1"><CalendarCheck size={16} weight="duotone" style={{ color: '#FF3B30' }} /><span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Pending Commission</span></div>
              <p className="text-2xl font-bold" style={{ color: '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>₹{earnings.pending_commission.toLocaleString()}</p>
            </div>
          </div>

          {/* Pay Commission Button */}
          {earnings.pending_commission > 0 && (
            <Button onClick={() => setShowPayDialog(true)} className="h-9 px-6 text-sm font-semibold rounded-md" style={{ background: '#34C759', color: 'white' }}>
              <HandCoins size={16} className="mr-2" /> Pay Commission (₹{earnings.pending_commission.toLocaleString()})
            </Button>
          )}

          <Tabs defaultValue="summary">
            <TabsList>
              <TabsTrigger value="summary">Monthly Summary</TabsTrigger>
              <TabsTrigger value="ledger">Ledger</TabsTrigger>
              <TabsTrigger value="settlements">Settlements</TabsTrigger>
            </TabsList>

            <TabsContent value="summary" className="space-y-4 mt-4">
              {/* Chart */}
              <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={earnings.monthly_breakdown || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E4E9" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="sales" fill="#007AFF" radius={[4,4,0,0]} name="Sales" />
                    <Bar dataKey="commission" fill="#34C759" radius={[4,4,0,0]} name="Commission" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Table */}
              <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
                <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  <thead><tr style={{ background: '#F9F9FA' }}>
                    {['Month', 'Orders', 'Sales', 'Commission'].map(h => (
                      <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {(earnings.monthly_breakdown || []).map((m, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #E2E4E9' }}>
                        <td className="px-4 py-2 font-medium">{m.month}</td>
                        <td className="px-4 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{m.orders}</td>
                        <td className="px-4 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{m.sales.toLocaleString()}</td>
                        <td className="px-4 py-2 font-medium" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>₹{m.commission.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="ledger" className="mt-4">
              <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
                <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  <thead><tr style={{ background: '#F9F9FA' }}>
                    {['Date', 'Type', 'Description', 'Amount'].map(h => (
                      <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {ledger.map((l, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #E2E4E9' }}>
                        <td className="px-4 py-2 text-xs" style={{ color: '#8A94A6' }}>{l.date}</td>
                        <td className="px-4 py-2"><span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: l.type === 'sale' ? '#E8F4FF' : '#F0FFF4', color: l.type === 'sale' ? '#007AFF' : '#34C759' }}>{l.type}</span></td>
                        <td className="px-4 py-2 text-xs">{l.description}</td>
                        <td className="px-4 py-2 font-medium text-right" style={{ fontVariantNumeric: 'tabular-nums', color: l.entry === 'credit' ? '#007AFF' : '#34C759' }}>₹{l.amount.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="settlements" className="mt-4">
              <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
                <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  <thead><tr style={{ background: '#F9F9FA' }}>
                    {['Date', 'Amount', 'Remarks'].map(h => (
                      <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {settlements.length === 0 ? (
                      <tr><td colSpan={3} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>No settlements yet</td></tr>
                    ) : settlements.map((s, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #E2E4E9' }}>
                        <td className="px-4 py-2 text-xs" style={{ color: '#8A94A6' }}>{s.settled_at?.slice(0, 10)}</td>
                        <td className="px-4 py-2 font-medium" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>₹{s.amount.toLocaleString()}</td>
                        <td className="px-4 py-2 text-xs">{s.remarks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>

          {/* Pay Commission Dialog */}
          <PayCommissionDialog open={showPayDialog} onClose={() => setShowPayDialog(false)} doctorId={selectedDoc} pending={earnings?.pending_commission || 0} doctorName={earnings?.doctor_name || ''} onPaid={() => { setShowPayDialog(false); loadData(); }} />
        </>
      )}
    </div>
  );
}

function PayCommissionDialog({ open, onClose, doctorId, pending, doctorName, onPaid }) {
  const [amount, setAmount] = useState(0);
  const [remarks, setRemarks] = useState('Commission settlement');
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => { setAmount(pending); setSuccess(false); }, [pending, open]);

  const pay = async () => {
    setPaying(true);
    try {
      await api.payDoctorCommission(doctorId, { amount, remarks });
      setSuccess(true);
    } catch {}
    setPaying(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Pay Commission</DialogTitle></DialogHeader>
        {success ? (
          <div className="p-6 text-center space-y-3">
            <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ background: '#F0FFF4' }}>
              <Check size={32} weight="bold" style={{ color: '#34C759' }} />
            </div>
            <p className="text-lg font-bold" style={{ color: '#34C759' }}>Payment Settled!</p>
            <p className="text-sm" style={{ color: '#8A94A6' }}>₹{amount.toLocaleString()} commission paid to {doctorName}</p>
            <p className="text-xs" style={{ color: '#8A94A6' }}>Entry added to Khata Book automatically</p>
            <Button onClick={onPaid} className="w-full h-9 text-sm" style={{ background: '#0A2540', color: 'white' }}>Done</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-3 rounded-md" style={{ background: '#F0F4F8' }}>
              <p className="text-xs" style={{ color: '#8A94A6' }}>Doctor: <strong style={{ color: '#0A2540' }}>{doctorName}</strong></p>
              <p className="text-xs" style={{ color: '#8A94A6' }}>Pending: <strong style={{ color: '#FF3B30' }}>₹{pending.toLocaleString()}</strong></p>
            </div>
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Amount to Pay</Label>
              <Input type="number" value={amount} onChange={e => setAmount(Number(e.target.value))} className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Remarks</Label>
              <Input value={remarks} onChange={e => setRemarks(e.target.value)} className="h-9 mt-1" />
            </div>
            <Button onClick={pay} disabled={paying || amount <= 0} className="w-full h-9 text-sm font-semibold" style={{ background: '#34C759', color: 'white' }}>
              <HandCoins size={16} className="mr-1" /> {paying ? 'Processing...' : `Pay ₹${amount.toLocaleString()}`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
